<template>
  <dropdown
    id="changelogButton"
    class="ui labeled icon pointing top upward purple button"
    :disabled="disabled"
    :active="showChangelog"
    @click="toggleChangelog"
  >
    <i class="history icon"></i>
    <span class="text">{{ $t('CHANGELOG') }}</span>
    <changelog-list
      id="changeLogList"
      ref="changelogList"
      class="ui transition menu"
      style="width: 400px;"
      :typename="typename"
      :itemId="currentItemId"
      :showNoChanges="true"
      :isDoubleSelect="false"
      :revert="onRevertAction"
      @outsideClick="hideChangelog"
      @changeChosen="onChangeChosen"
      :mainStyle="{ 'min-height': '300px', 'max-height': 'calc(100vh - 180px)' }"
      :innerStyle="{ 'max-height': 'calc(100vh - 350px)' }"
    />
  </dropdown>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

#changeLogList {
  left: auto !important;
  right: 0 !important;

  -webkit-transition: opacity 300ms ease-out;
  -moz-transition: opacity 300ms ease-out;
  -ms-transition: opacity 300ms ease-out;
  -o-transition: opacity 300ms ease-out;
  transition: opacity 300ms ease-out;
}

.ui.pointing.dropdown > .menu:after {
  left: 85% !important;
}
</style>
<script>
// @flow
import Vue from 'vue'
import { Toast } from 'services'
import ChangelogList from '../Changelog/ChangelogList'
import { Dropdown } from 'ui'
import { create as createDiffer } from 'jsondiffpatch'
import flatten, { unflatten } from 'flat'

import _ from 'lodash'

const differ = createDiffer({
  textDiff: {
    minLength: Number.MAX_VALUE
  }
})

// func to remove empty arrays during diffing
// which provide no diff info and
// mess up the flatten/unflatten results
const removeEmptyArrays = flattened => {
  return _.transform(
    flattened,
    (acc, v, k) => {
      if (!(_.isArray(v) && _.isEmpty(v))) {
        acc[k] = v
      }
      return acc
    },
    {}
  )
}

export default {
  name: 'changelog-button',
  components: { ChangelogList, Dropdown },

  props: {
    typename: {
      type: String,
      default: () => ''
    },
    item: {
      type: Object,
      default: () => {}
    },
    itemType: {
      type: String,
      default: () => ''
    },
    saveFn: {
      type: Function,
      default: () => new Promise()
    },
    disabled: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      showChangelog: false,
      originalItem: {},
      currentItemId: this.item.id,
      currentChange: {},
      isRevertInProgress: false,
      revertData: null,
      toggleChangelog: _.debounce(() => (this.showChangelog = !this.showChangelog), 100, {
        trailing: true
      })
    }
  },

  methods: {
    // Refresh changes
    refreshChangesData() {
      // TODO remove this invocation on the changelogList component
      if (this.$refs.changelogList) this.$refs.changelogList.refreshComponentData()
    },

    // Hide the changelog if it is shown
    hideChangelog() {
      if (this.$refs.changelogList && this.showChangelog) {
        this.showChangelog = false
      }
    },

    // Revert and reset changelog
    abort() {
      this.revertToOriginalItem()
      this.resetChangelogList()
    },

    // Reset changelog will clear selection and hide it
    resetChangelogList() {
      // TODO remove this invocation on the changelogList component
      this.$refs.changelogList.clearChosenChanges()
      this.hideChangelog()
    },

    // Abort the revert process
    revertToOriginalItem() {
      if (!_.isEmpty(this.originalItem)) {
        this.$emit('change-chosen', {
          changedItem: _.cloneDeep(this.originalItem),
          flaggedDiff: {},
          fullDiff: {},
          originalItem: {},
          abort: true,
          log: {},
          actions: { revert: () => {}, abort: () => {} }
        })
        this.originalItem = {}
        this.revertData = null
      }
    },

    // Handler for when a change is chosen in the changelog list
    onChangeChosen(change, data) {
      if (_.isEmpty(this.originalItem)) this.originalItem = { ...this.item }

      // If the current change is selected again we return to normal
      if (!change.identifier) {
        this.abort()
        return
      }

      if (!_.isEmpty(change)) {
        this.currentChange = change
        this.revertData = data

        let { flaggedDiff, fullDiff, originalItem, changedItem } = this.produceDiff(
          this.originalItem,
          change
        )

        const _this = this
        this.$emit('change-chosen', {
          changedItem,
          flaggedDiff,
          originalItem,
          fullDiff,
          log: change,
          actions: {
            revert: _.once(() => _this.onRevertAction(data)),
            abort: _.once(() => _this.abort())
          }
        })
      }
    },

    // Produce diff information.
    // Will produce;
    // changedItem: is the originalItem with the diff applied
    // flaggedDiff: is an object mirroring the structure of originalItem but with bool values
    // where true indicates a change
    // originalItem: is the object from which the diff was calculated
    // fullDiff: is an object where each leaf property has a new and old value
    // a callback object containing revert and abort actions
    produceDiff(originalItem, change) {
      let base = _.cloneDeep(originalItem)
      let changed = _.cloneDeep({ ...change, ...change.descriptive, id: base.id })

      // Get rid of misc cruft
      //delete base.id
      delete base.version
      delete changed.idx
      delete changed.tag
      delete changed.ts
      delete changed.identifier
      delete changed.descriptive

      // TODO removing an array and adding another is seen as a change since e.g. index 0 is changed from x to y -- we'd

      // Empty arrays are removed bc they mess up the diff when flattened
      const flatBase = removeEmptyArrays(flatten(base))
      const flatChanged = removeEmptyArrays(flatten(changed))

      // Construct the actual diff (see format at https://github.com/benjamine/jsondiffpatch/blob/master/docs/deltas.md)
      const diff = differ.diff(flatBase, flatChanged) || {}

      // Build flat versions for easier diff building
      // Array diffs are indexed with _+index if the change is in the original array
      // i.e. for **deletions**, we're going to normalize this
      const flatDiff = _.mapKeys(diff, (_, k) => k.replaceAll(/\._(\d+)/g, '.$1'))

      // We need an object that contains all properties of base and changed
      const combined = _.assign(flatBase, flatChanged)

      // Construct flagged diff by traversing the original object and setting
      // - true if changed
      // - false otherwise
      const flaggedDiff = unflatten(
        _.mapValues(combined, (_, k) => flatDiff[k] !== undefined && flatDiff[k] !== null)
      )

      // The fullDiff contains { new, old } values where new is `undefined` if not changed
      const fullDiff = unflatten(
        _.mapValues(combined, (__, k) => {
          // additions are [newval]
          const isAddition = flatDiff[k] && flatDiff[k].length === 1
          // updates are [oldval, newval]
          const isUpdate = flatDiff[k] && flatDiff[k].length === 2
          // deletions are [oldval, 0, 0]
          const isDeletion =
            (flatDiff[k] &&
              flatDiff[k].length == 3 &&
              flatDiff[k][1] === 0 &&
              flatDiff[k][2] === 0) === true
          // hasChange covers all changes
          const hasChange = isUpdate || isAddition || isDeletion
          return {
            old: isUpdate || isDeletion ? _.first(flatDiff[k]) : flatBase[k],
            new: isUpdate || isAddition ? _.last(flatDiff[k]) : flatChanged[k],
            hasChange,
            isDeletion,
            isAddition,
            isUpdate
          }
        })
      )

      changed.isDiff = true

      return { flaggedDiff, fullDiff, originalItem: base, changedItem: changed }
    },

    // Revert to a selected version of the item
    onRevertAction(revertedData) {
      this.resetChangelogList()
      this.isRevertInProgress = true
      let itemToRevertTo = this.mapRevertedData(revertedData)
      this.$emit('revert', itemToRevertTo)
      return this.saveFn()
        .then(() => {
          this.originalItem = {}
          this.isRevertInProgress = false
          Toast.success(this.$i18n.t('REVERTED', { thing: this.$i18n.tc(this.itemType, 1) }), {
            remove: 5000
          })
        })
        .catch(err => {
          console.error(err)
          this.revertToOriginalItem()
          this.isRevertInProgress = false
          Toast.error(this.$i18n.t('ERR', { action: this.$i18n.t('REVERT') }))
        })
        .finally(() => {
          this.revertData = null
        })
    },
    mapRevertedData(revertedData) {
      delete revertedData.isDiff
      delete revertedData.version
      const revertedDescriptive = revertedData.descriptive
      delete revertedData.descriptive
      let mappedData = { ...revertedData, ...revertedDescriptive }
      delete mappedData.isDiff
      mappedData.version = this.originalItem.version
      return mappedData
    }
  },

  mounted() {
    this.refreshChangesData()
  },

  watch: {
    // TODO consider cleaning this up as well
    item: {
      handler: function(newItem) {
        if (!this.currentItemId) {
          this.currentItemId = newItem.id
          this.refreshChangesData()
        } else if (!newItem.isDiff && this.currentItemId !== newItem.id) {
          this.currentItemId = newItem.id
          this.hideChangelog()
          this.originalItem = {}
          if (this.$refs.changelogList) this.$refs.changelogList.clearChanges()
        }
      },
      deep: true
    }
  }
}
//
</script>
